import {type RefObject, useCallback, useState} from 'react';
import {useImageAnalyzeStep} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {ENTRY_SOURCE_TYPE_IMAGE_ANALYSIS} from '../../../constants/entrySourceTypes';
import {
  buildAnalyzeStepParams,
  showAnalyzeError,
} from '../../../usecase/analyzeStepHelpers';
import {ScreenshotUploadModal} from '../Presenter/Progress/ScreenshotUploadModal';
import type {StepCandidatePreviewModalRef} from './StepCandidatePreviewModalContainer';

type Props = {
  opened: boolean;
  onClose: () => void;
  stepCandidatePreviewModalRef?: RefObject<StepCandidatePreviewModalRef>;
  companyId?: number;
  entryManagementId?: number;
  onSuccess?: () => void;
  onBack?: () => void;
};

/**
 * ファイルをBase64エンコードする
 * data:image/png;base64, のプレフィックスを削除して返す
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1] || '';
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * ファイル拡張子から画像フォーマットを取得する
 */
const getImageFormat = (
  file: File,
): 'jpeg' | 'jpg' | 'png' | 'gif' | 'webp' => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const validFormats = ['jpeg', 'jpg', 'png', 'gif', 'webp'] as const;

  if (validFormats.includes(extension as (typeof validFormats)[number])) {
    return extension as 'jpeg' | 'jpg' | 'png' | 'gif' | 'webp';
  }

  // デフォルトはpng
  return 'png';
};

export const ScreenshotUploadModalContainer: React.FC<Props> = ({
  opened,
  onClose,
  stepCandidatePreviewModalRef,
  companyId,
  entryManagementId,
  onSuccess,
  onBack,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const imageAnalyzeStepMutation = useImageAnalyzeStep();

  const resetState = useCallback(() => {
    setFile(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
    onBack?.();
  }, [resetState, onClose, onBack]);

  const handleSuccess = useCallback(() => {
    resetState();
    onClose();
    onSuccess?.();
  }, [resetState, onClose, onSuccess]);

  const handleSubmit = async (uploadedFile: File) => {
    if (!uploadedFile) {
      return;
    }

    try {
      const imageBase64 = await fileToBase64(uploadedFile);
      const imageFormat = getImageFormat(uploadedFile);

      const result = await imageAnalyzeStepMutation.mutateAsync({
        imageBase64,
        imageFormat,
        ...buildAnalyzeStepParams(companyId, entryManagementId),
      });

      if (!result.ok) {
        showAnalyzeError('画像');
        return;
      }

      // スクリーンショットアップロードモーダルを閉じる
      handleSuccess();

      // 画像解析結果をプレビューモーダルに渡す
      if (stepCandidatePreviewModalRef?.current) {
        stepCandidatePreviewModalRef.current.open(
          result.value,
          ENTRY_SOURCE_TYPE_IMAGE_ANALYSIS,
        );
      }
    } catch (error) {
      showAnalyzeError('画像', error);
    }
  };

  return (
    <ScreenshotUploadModal
      opened={opened}
      onClose={handleClose}
      onSubmit={handleSubmit}
      file={file}
      onFileChange={setFile}
      isLoading={imageAnalyzeStepMutation.isLoading}
      error={error}
      onError={setError}
    />
  );
};
